/**
 * The app shell (Organisations UI, S1 — docs/engineering/ORG_UI_DESIGN_REVIEW.md).
 *
 *   header   ☰ · Learnbee logo / organisation pill ⌄ · bell · name + avatar
 *   sidebar  Home · Courses · Templates · Library  |  Manage (Owner/Admin):
 *            Members · Attributes · User groups · Reports · Settings
 *   main     the page (React Router <Outlet/>)
 *
 * Used on every page except the course editor, wizard, Translate and the
 * preview player. Pages inside it skip their own header (useInShell).
 * Only mounted when FEATURE_ORGANISATIONS is on — production is unchanged.
 */

import { NoAccess } from './NoAccess'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { UserButton, useUser } from '@clerk/clerk-react'
import { OrgDetailsPanel } from '@/components/attributes/OrgDetailsPanel'
import { ShellLoading } from './ShellLoading'
import { NotificationBell } from '@/lib/notifications'
import { useOrgs } from '@/lib/useOrgs'
import { T, mainRole } from './orgTheme'
import { Skeleton } from '@/components/org/orgUi'
import { OrgTile } from './OrgTile'
import { OrgMenu } from './OrgMenu'
import { InShell } from './inShell'
import { spaLink } from '@/lib/spaLink'
import { TransferBanner } from './TransferBanner'
import { WhatsNewDialog } from './WhatsNewDialog'
import { orgApi } from '@/lib/orgApi'
import { NavIcon, type NavIconName } from './NavIcons'


interface NavItem { label: string; path: string; icon: NavIconName; match?: string[] }

/** Where the desktop sidebar's open / collapsed choice is kept. */
const SIDEBAR_KEY = 'learnbee_sidebar'

export function AppShell() {
  const orgs = useOrgs()
  const { user } = useUser()
  const { pathname, state: navState } = useLocation()
  // A page opened from a bell note loads fresh (20 Oct 2026): Members was already open, so
  // following “Review request” kept its old list until a reload.
  const fresh = (navState as { fresh?: number } | null)?.fresh ?? ''
  const navigate = useNavigate()
  // "What's new" after adoption — or, on localhost only, a preview with sample numbers:
  // /?whatsnew (with work) or /?whatsnew=empty (nothing moved). It changes no data.
  const previewWhatsNew = import.meta.env.DEV && typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('whatsnew') : null
  const [previewOpen, setPreviewOpen] = useState(previewWhatsNew !== null)
  const whatsNew = orgs.adoptedNow ?? (previewOpen
    ? (previewWhatsNew === 'empty' ? { courses: 0, images: 0, videos: 0, pdfs: 0 } : { courses: 42, images: 197, videos: 12, pdfs: 7 })
    : null)
  const seenWhatsNew = () => { orgs.markWhatsNewSeen(); setPreviewOpen(false) }
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768)
  // Desktop: open or collapsed to the rail — remembered in this browser across reloads (13 Oct 2026).
  const [sidebar, setSidebarState] = useState<boolean>(() => {
    try { return localStorage.getItem(SIDEBAR_KEY) !== 'collapsed' } catch { return true }
  })
  const setSidebar = (next: boolean | ((v: boolean) => boolean)) => setSidebarState(v => {
    const n = typeof next === 'function' ? next(v) : next
    try { localStorage.setItem(SIDEBAR_KEY, n ? 'open' : 'collapsed') } catch { /* private mode: it just isn't remembered */ }
    return n
  })
  const [mNav, setMNav] = useState(false)           // mobile: overlay open
  const [detailsOpen, setDetailsOpen] = useState(false)
  // <main> is the scroll area, not the window, so the router never resets it: opening a
  // page from a long list landed part-way down. Every new page starts at the top.
  const mainRef = useRef<HTMLElement>(null)
  useLayoutEffect(() => { mainRef.current?.scrollTo({ top: 0 }) }, [pathname])
  // Where the org menu opens: under the pill, measured when it is clicked (null = closed).
  const [menuLeft, setMenuLeft] = useState<number | null>(null)
  const menuOpen = menuLeft !== null
  const setMenuOpen = (open: boolean) => setMenuLeft(open ? 140 : null)

  useEffect(() => {
    const on = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', on)
    return () => window.removeEventListener('resize', on)
  }, [])
  // Navigating closes the mobile nav and the org menu (compared during render, not in an effect).
  const [navPath, setNavPath] = useState(pathname)
  if (navPath !== pathname) { setNavPath(pathname); setMNav(false); setMenuLeft(null) }

  const main: NavItem[] = orgs.isLearnerOnly
    // A Learner's Courses are the courses launched to them (12 Oct 2026) — the same place
    // everyone else finds them, as the My learning tab.
    ? [{ label: 'Home', path: '/', icon: 'home' }, { label: 'Courses', path: '/courses', icon: 'courses', match: ['/learn'] }, { label: 'Library', path: '/library', icon: 'library' }]
    : [
        { label: 'Home', path: '/', icon: 'home' },
        { label: 'Courses', path: '/courses', icon: 'courses', match: ['/learn'] },
        { label: 'Templates', path: '/templates', icon: 'templates' },
        { label: 'Library', path: '/library', icon: 'library' },
      ]
  const manage: NavItem[] = orgs.isManager
    ? [
        { label: 'Members', path: '/org/members', icon: 'members', match: ['/org/invite'] },
        { label: 'Attributes', path: '/org/attributes', icon: 'attributes' },
        { label: 'User groups', path: '/org/groups', icon: 'groups' },
        // Reports before Settings: Settings is the one people visit least, so it goes last (9 Oct 2026).
        { label: 'Reports', path: '/org/reports', icon: 'reports' },
        { label: 'Settings', path: '/org/settings', icon: 'settings' },
      ]
    : []
  const isOn = (p: string) => (p === '/' ? pathname === '/' : pathname === p || pathname.startsWith(p + '/'))

  const cur = orgs.current
  const meName = user?.fullName || user?.primaryEmailAddress?.emailAddress || ''
  const showSidebar = isMobile ? mNav : sidebar

  // Nothing is drawn until the first organisation check is back. Otherwise the page
  // flashes before sign-up's redirect to setup, or briefly in the wrong organisation.
  if (orgs.status === 'loading' && orgs.orgs.length === 0) return <ShellLoading />
  // Sign-up just made their organisation: set it up first (once — it can be skipped).
  if (orgs.justCreated) return <Navigate to="/org/setup" replace />
  // A first sign-in with invitations waiting: Join, or create their own — nothing made yet.
  if (orgs.invitedFirst) return <Navigate to="/org/invited" replace />
  // No organisation left — every membership paused, or removed (G2 / G3) — or none yet, only a join
  // request waiting for an admin (15): explain, don't open empty.
  if (orgs.status === 'ready' && orgs.orgs.length === 0 && (orgs.paused.length > 0 || orgs.removed.length > 0 || orgs.requested.length > 0)) return <NoAccess />

  return (
    <InShell.Provider value={true}>
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: T.page }}>
        {/* The ☰ (36px button, left 16) is centred 34px from the edge — the same line as
            the sidebar icons, open or collapsed to the rail. Its visible lines start 28px
            in (16 + 9 inside the button + 3 inside the icon), so the right padding is 28:
            the avatar sits exactly as far from the right edge as the ☰ from the left. */}
        <header style={{ height: 56, flex: 'none', display: 'flex', alignItems: 'center', gap: 10, padding: `0 ${isMobile ? 16 : 28}px 0 16px`, background: '#fff', borderBottom: `1px solid ${T.border}`, position: 'relative', zIndex: 30 }}>
          <button onClick={() => (isMobile ? setMNav(v => !v) : setSidebar(v => !v))} aria-label="Menu"
            className="hover:bg-[#F4F2F6]"
            style={{ width: 36, height: 36, flex: 'none', display: 'grid', placeItems: 'center', border: 'none', background: 'transparent', borderRadius: 8, cursor: 'pointer', color: T.ink }}>
            <svg width="18" height="18" viewBox="0 0 18 18"><path d="M3 5h12M3 9h12M3 13h12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
          </button>
          {!isMobile && (
            <>
              <a {...spaLink('/', () => navigate('/'))} aria-label="Home" style={{ display: 'flex' }}>
                <img src="/learnbee-logo.png" alt="Learnbee" style={{ height: 21, width: 'auto' }} />
              </a>
              {cur && <span style={{ color: '#D6D1DD', fontSize: 20, fontWeight: 300, margin: '0 2px' }}>/</span>}
            </>
          )}
          {cur && (
            <button onClick={e => { const x = e.currentTarget.offsetLeft; setMenuLeft(l => (l === null ? x : null)) }} aria-haspopup="menu" aria-expanded={menuOpen}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 9px 5px 5px', borderRadius: 9, border: 'none', background: T.tint, boxShadow: `0 0 0 1px ${T.tintBorder}`, cursor: 'pointer', minWidth: 0 }}>
              <OrgTile id={cur.id} name={cur.name} logoUrl={cur.logoUrl} size={26} radius={6} fontSize={11} />
              <span style={{ fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: isMobile ? 120 : 220, color: T.dark }}>{cur.name}</span>
              <span style={{ fontSize: 11.5, padding: '2px 7px', borderRadius: 999, background: '#fff', color: T.purpleText, fontWeight: 500 }}>{mainRole(cur.roles)}</span>
              <svg width="10" height="10" viewBox="0 0 10 10" style={{ flex: 'none', color: T.muted }}><path d="M2 3.5l3 3 3-3" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" /></svg>
            </button>
          )}
          {orgs.status === 'loading' && !cur && (
            <span aria-label="Loading organisations"><Skeleton w={160} h={28} r={9} /></span>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 14 }}>
            <NotificationBell />
            {!isMobile && meName && <span style={{ fontSize: 13.5, color: T.ink }}>{meName}</span>}
            <UserButton afterSignOutUrl="/sign-in">
              {/* The member's own organisation details (attributes + groups) — design preview. Opens a
                  90% side panel (5 Oct 2026), not a page inside Clerk's account popup. */}
              {cur && (
                <UserButton.MenuItems>
                  <UserButton.Action label="Organisation details" labelIcon={<IdCardIcon />} onClick={() => setDetailsOpen(true)} />
                </UserButton.MenuItems>
              )}
            </UserButton>
            {detailsOpen && <OrgDetailsPanel onClose={() => setDetailsOpen(false)} />}
          </div>
          {menuOpen && <OrgMenu anchorLeft={isMobile ? 8 : (menuLeft ?? 140)} width={isMobile ? 'calc(100% - 16px)' : 360} onClose={() => setMenuOpen(false)} />}
        </header>

        {orgs.status === 'error' && (
          <div role="alert" style={{ padding: '10px 16px', background: T.redBg, color: T.red, fontSize: 13.5, display: 'flex', gap: 12, alignItems: 'center' }}>
            <span>Couldn't load your organisations. {orgs.error}</span>
            <button onClick={() => void orgs.reload()} style={{ border: `1px solid ${T.red}`, background: '#fff', color: T.red, borderRadius: 7, padding: '4px 10px', fontSize: 13, cursor: 'pointer' }}>Try again</button>
          </div>
        )}

        {orgs.transfers.map(t => <TransferBanner key={t.id} t={t} />)}
        {/* An existing user's work was moved into their organisation on this sign-in (go-live,
            25 Oct 2026): "What's new", once — instead of setup. Every exit marks it seen. */}
        {whatsNew && cur && (
          <WhatsNewDialog open firstName={user?.firstName ?? null} orgName={cur.name} counts={whatsNew}
            onRename={orgs.adoptedNow
              ? async name => { await orgApi.update(cur.id, { name }); orgs.patchCurrent({ name }) }
              // The localhost preview pretends to save (a name with "fail" in it shows the error state).
              : name => new Promise((ok, no) => setTimeout(() => (/fail/i.test(name) ? no(new Error('preview')) : ok()), 900))}
            // Get started → Home, whatever moved (25 Oct 2026, Jasmine; the handoff had My courses).
            onGetStarted={() => { seenWhatsNew(); navigate('/') }}
            onInvite={() => { seenWhatsNew(); navigate('/org/invite') }}
            onClose={seenWhatsNew} />
        )}

        <div style={{ flex: 1, display: 'flex', minHeight: 0, position: 'relative' }}>
          {isMobile && mNav && <div onClick={() => setMNav(false)} style={{ position: 'absolute', inset: 0, background: 'rgba(28,26,34,.3)', zIndex: 19 }} />}
          {/* Desktop ☰ switches between the full sidebar and an icon rail; mobile opens it over the page. */}
          {!isMobile && !sidebar && (
            <aside aria-label="Navigation" style={{ width: 68, flex: 'none', background: '#fff', borderRight: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '14px 0', gap: 4, overflowY: 'auto' }}>
              <Nav items={main} isOn={isOn} go={navigate} rail />
              {manage.length > 0 && <div style={{ width: 28, height: 1, background: T.border, margin: '8px 0' }} />}
              <Nav items={manage} isOn={isOn} go={navigate} rail />
            </aside>
          )}
          {showSidebar && (
            <aside style={{ width: 232, flex: 'none', background: '#fff', borderRight: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', padding: '18px 12px', gap: 18, position: isMobile ? 'absolute' : 'relative', top: 0, bottom: 0, left: 0, zIndex: 20, boxShadow: isMobile ? '0 10px 40px rgba(0,0,0,.2)' : 'none', overflowY: 'auto' }}>
              {/* No organisation header here (15 Oct 2026): the top bar's switcher already shows it, right above. */}
              <Nav items={main} isOn={isOn} go={navigate} />
              {manage.length > 0 && (
                <nav style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <div style={{ padding: '0 12px 6px', fontSize: 11.5, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: T.faint }}>Manage</div>
                  <Nav items={manage} isOn={isOn} go={navigate} bare />
                </nav>
              )}
            </aside>
          )}
          {/* On desktop (full sidebar or icon rail) the page gets extra room on both
              sides, on top of each page's own padding. */}
          <main ref={mainRef} style={{ flex: 1, minWidth: 0, overflowY: 'auto', position: 'relative', padding: !isMobile ? '0 24px' : 0, ['--shell-px' as string]: !isMobile ? '24px' : '0px' }}>
            {/* Keyed by the organisation: switching re-opens the page, so it loads that organisation's content. */}
            <Outlet key={`${orgs.current?.id ?? 'none'}:${fresh}`} />
          </main>
        </div>
      </div>
    </InShell.Provider>
  )
}

function Nav({ items, isOn, go, bare, rail }: { items: NavItem[]; isOn: (p: string) => boolean; go: (p: string) => void; bare?: boolean; rail?: boolean }) {
  const list = items.map(n => {
    const on = isOn(n.path) || !!n.match?.some(isOn)
    return (
      <a key={n.path} {...spaLink(n.path, () => go(n.path))} aria-current={on ? 'page' : undefined}
        aria-label={rail ? n.label : undefined} title={rail ? n.label : undefined}
        className={on ? undefined : 'hover:bg-[#F4F2F6]'}
        style={rail
          ? { width: 44, height: 44, display: 'grid', placeItems: 'center', borderRadius: 10, border: 'none', background: on ? T.tint : 'transparent', color: on ? T.purple : T.muted, cursor: 'pointer', flex: 'none' }
          : { display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left', padding: '9px 12px', borderRadius: 9, border: 'none', background: on ? T.tint : 'transparent', color: on ? T.purpleText : T.ink, fontWeight: on ? 600 : 400, cursor: 'pointer', fontSize: 14, textDecoration: 'none' }}>
        <span style={{ display: 'grid', color: on ? T.purple : T.muted }}><NavIcon name={n.icon} active={on} /></span>
        {!rail && n.label}
      </a>
    )
  })
  if (rail) return <>{list}</>
  return bare ? <>{list}</> : <nav style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 14 }}>{list}</nav>
}

/** Shown for the moment the first organisation check takes — logo only, no page behind it. */

/** The ID-card icon (as on the Attributes nav item), sized for Clerk's menu. */
function IdCardIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" style={{ display: 'block' }}>
      <rect x="3" y="5" width="18" height="14" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="8.6" cy="10.4" r="1.8" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M5.9 16.1c.5-1.4 1.5-2.1 2.7-2.1s2.2.7 2.7 2.1M13.6 10.2h4.2M13.6 13.8h4.2" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  )
}
